import axios from 'axios'
import wcs from './index'
import { WcsPromotions } from './promotions'
import { WcsPromoToggle } from './types/plp-promo-toggles'
import { PromoToggleHelper } from '@/scripts/libs/promo-toggles'
import { PromoToggle } from '@/types/plp'
import {
  ALL_PROMOS,
  ALL_PROMOS_LABEL,
  FLASH_SALE_LABEL,
  PST_PROMOS,
  PST,
  SCARCITY_PROMO,
  SCARCITY,
} from '@/scripts/constants/promos'
import { WcsUser } from '@/scripts/libs/wcs/user'
import RbDate from '@/utils/rb-date'

class WcsPromoToggles implements PromoToggleHelper {
  getPromoToggles = async (): Promise<PromoToggle[]> => {
    const { allPromoLabel, flashSalesLabel, allPromoToggleVisibility, promotions } =
      await this.fetchPromoToggles()

    const filteredPromoToggles = this.filterPromoToggles(promotions)
    return this.mapPromotions(
      filteredPromoToggles,
      allPromoLabel,
      flashSalesLabel,
      allPromoToggleVisibility
    )
  }

  filterPromoToggles = (promotions: WcsPromoToggle[]) => {
    return promotions.filter((promo) => {
      if (!promo.customerSegmentVisibility || promo.customerSegmentVisibility === 'ALL') {
        return promo
      }
      if (promo.customerSegmentVisibility !== 'NONE') {
        const userCluster = WcsUser.getMergedUserCluster()
        return (
          !promo?.customerSegmentVisibility ||
          promo.customerSegmentVisibility.length === 0 ||
          userCluster?.some((cluster) => promo?.customerSegmentVisibility?.includes(cluster))
        )
      }
    })
  }

  sortPromotions = (originalOrder: string[], promoToggles: PromoToggle[]): PromoToggle[] => {
    const sortedPromotions = promoToggles.sort((currentPromo, nextPromo) => {
      if (currentPromo.id === ALL_PROMOS) {
        return -1
      }
      const currentEntryValue = `${currentPromo.entryValue[0]}`
      const nextEntryValue = `${nextPromo.entryValue[0]}`
      return originalOrder.indexOf(currentEntryValue) - originalOrder.indexOf(nextEntryValue)
    })
    return sortedPromotions
  }

  normalizePromoToggles = (promotions: WcsPromoToggle[]): WcsPromoToggle[] => {
    if (!promotions || !Array.isArray(promotions) || promotions.length === 0) {
      return []
    }
    return promotions.map(({ startDate, endDate, ...rest }) => {
      const startDateTimestamp = RbDate.getTimeDate(new Date(WcsPromotions.sanitizeDate(startDate)))
      const endDateTimestamp = RbDate.getTimeDate(new Date(WcsPromotions.sanitizeDate(endDate)))
      return {
        ...rest,
        startDate,
        endDate,
        startDateTimestamp,
        endDateTimestamp,
      }
    })
  }

  fetchPromoToggles = async () => {
    const endpoint = wcs.getEndPoint('promoToggleURL')
    const response = await axios.get(endpoint)
    const promotions = this.normalizePromoToggles(response?.data?.pstPriceList)

    const { allPromoLabel, flashSalesLabel, allPromoToggleVisibility } = response?.data

    return {
      promotions,
      allPromoLabel,
      flashSalesLabel,
      allPromoToggleVisibility,
    }
  }

  mapPromotions = (
    promotions: WcsPromoToggle[],
    allPromoLabel = ALL_PROMOS_LABEL,
    flashSalesLabel = FLASH_SALE_LABEL,
    allPromoToggleVisibility: string
  ) => {
    const mappedScarcityPromotions = this.mapScarcityPromotions(promotions, flashSalesLabel)
    const mappedPstPromotions = this.mapPstPromotions(promotions)
    let result: PromoToggle[] = []

    const displayAllPromosToggle = this.displayAllPromosToggleIsVisible(allPromoToggleVisibility)

    if (displayAllPromosToggle) {
      result = [
        {
          id: ALL_PROMOS,
          badge: allPromoLabel,
          dataElID: 'X_SunPLP_0-Filter_AllPromos',
          isFlash: false,
          entryValue: [],
          icon: ALL_PROMOS,
          name: ALL_PROMOS_LABEL,
          endDate: '',
          endDateTimestamp: 0,
          startDate: '',
          startDateTimestamp: 0,
          type: '',
          userSegments: '',
        },
        ...mappedScarcityPromotions,
        ...mappedPstPromotions,
      ]
    } else {
      result = [...mappedScarcityPromotions, ...mappedPstPromotions]
    }
    const promos = result.filter(
      (promo) =>
        !promo.endDate ||
        (promo.endDate && !WcsPromotions.isPromotionEnded(promo.endDateTimestamp ?? 0))
    )
    return promos
  }

  /* TODO: Extract this in abstract class */
  private displayAllPromosToggleIsVisible = (displayAllPromosToggle: string) => {
    if (displayAllPromosToggle === 'ALL') {
      return true
    }
    if (displayAllPromosToggle === 'NONE') {
      return false
    } else {
      const userCluster = WcsUser.getMergedUserCluster()
      return (
        displayAllPromosToggle &&
        userCluster?.some((cluster) => displayAllPromosToggle?.includes(cluster))
      )
    }
  }

  private mapPstPromotions = (promotions: WcsPromoToggle[]): PromoToggle[] => {
    const pstPromotions = promotions?.filter((promo) => promo.type === PST)

    return pstPromotions.map((promo, index) => {
      return {
        id: `${PST_PROMOS}_${index}`,
        badge: promo.badge,
        entryValue: [promo.command],
        isFlash: false,
        dataElID: `X_SunPLP_0-${promo.name}`,
        icon: PST_PROMOS,
        startDate: promo.startDate,
        startDateTimestamp: promo.startDateTimestamp,
        endDate: promo.endDate,
        endDateTimestamp: promo.endDateTimestamp,
        userSegments: promo.userSegments,
        name: promo.name,
        type: promo.type,
      }
    })
  }

  private mapScarcityPromotions = (promotions: WcsPromoToggle[], badge: string): PromoToggle[] => {
    const scarcityPromotions = promotions.filter((promo) => {
      const isScarcity = promo.type === SCARCITY
      const isScarcityEnded = WcsPromotions.isPromotionEnded(promo.endDateTimestamp)
      return isScarcity && !isScarcityEnded
    })

    if (scarcityPromotions && Array.isArray(scarcityPromotions) && scarcityPromotions.length > 0) {
      return scarcityPromotions.map((promo, index) => {
        return {
          id: `${SCARCITY_PROMO}_${index}`,
          badge,
          dataElID: 'X_SunPLP_0-Filter_FlashSales',
          isFlash: true,
          entryValue: [promo.command],
          startDate: promo.startDate,
          startDateTimestamp: promo.startDateTimestamp,
          endDate: promo.endDate,
          endDateTimestamp: promo.endDateTimestamp,
          userSegments: promo.userSegments,
          name: promo.name,
          type: promo.type,
          icon: '',
        }
      })
    } else {
      return []
    }
  }
}

export default new WcsPromoToggles()
